'use client';

import React, { Component, ReactNode } from 'react';
import logger from '@/lib/logger';
import { reportCrashScreenShown } from '@/lib/telemetry/uiRenderTelemetry';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    // A142: this boundary's fallback is a crash screen.
    reportCrashScreenShown('error_boundary', error);
    // Log to server via API
    logger.error('ErrorBoundary', error.message, {
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      name: error.name,
    });
  }

  render() {
    if (this.state.hasError) {
      const showErrorDetails = process.env.NODE_ENV !== 'production';
      return (
        <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-slate-800 rounded-lg p-6 border border-blue-500">
            <h2 className="text-xl font-bold text-blue-400 mb-4">Something went wrong</h2>
            <p className="text-slate-300 mb-4">
              The application encountered an error. This has been logged and will be investigated.
            </p>
            {showErrorDetails && this.state.error && (
              <details className="mb-4">
                <summary className="cursor-pointer text-sm text-slate-400 hover:text-slate-300">
                  Error details
                </summary>
                <pre className="mt-2 text-xs bg-slate-900 p-3 rounded overflow-auto max-h-48">
                  {this.state.error.message}
                  {'\n\n'}
                  {this.state.error.stack}
                </pre>
              </details>
            )}
            <button
              onClick={() => window.location.reload()}
              className="cursor-pointer w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded transition-colors"
            >
              Reload Application
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
